import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BellRing, Cake, CalendarClock, Check, CloudRain, Coins, Heart, Star, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead, Panel, Stat, WaButton, itDate, useList } from "@/components/kit";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { WeatherPanel } from "@/components/WeatherPanel";
import { PendingExtras } from "@/components/UniquePanel";

const ICONS = { rata: Coins, ricorrenza: Cake, appuntamento: CalendarClock, ospiti: Users, numero: Users, conferma: Check, recensione: Star, opzione: AlertTriangle, preventivo: BellRing, richiesta: BellRing, meteo: CloudRain };
const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function Reminder({ r, done }) {
  const Icon = r.kind === "ricorrenza" && r.title.startsWith("Anniversario") ? Heart : ICONS[r.kind] || BellRing;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-[#EAF5F7] last:border-0" data-testid={`reminder-${r.key}`}>
      <div className="h-10 w-10 shrink-0 rounded-full bg-[#EAF5F7] text-[#0B6E8F] flex items-center justify-center"><Icon size={17} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{r.title} <span className="text-[#7B929C] font-normal">• {r.nome}</span></div>
        <div className="text-xs text-[#7B929C]">{itDate(r.date)} • {r.detail}</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {r.link && <Link to={r.link} className="text-xs px-3 py-1.5 rounded-full border border-[#D4E6EC] hover:border-[#0B6E8F]" data-testid={`reminder-open-${r.key}`}>Apri</Link>}
        {!r.internal && <WaButton phone={r.tel} text={r.message} testId={`reminder-wa-${r.key}`} label="Invia" />}
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => done(r.key)} data-testid={`reminder-done-${r.key}`}><Check size={14} className="mr-1" /> Fatto</Button>
      </div>
    </div>
  );
}

function MonthsChart({ months }) {
  const max = Math.max(1, ...months.map((m) => m.eventi));
  return (
    <div className="flex items-end gap-2 h-40" data-testid="months-chart">
      {months.map((m) => (
        <div key={m.mese} className="flex-1 flex flex-col items-center gap-1" title={`${m.eventi} eventi • ${fmtMoney(m.valore)}`}>
          <div className="text-[10px] text-[#47606B]">{m.eventi || ""}</div>
          <div className="w-full rounded-t-md bg-gradient-to-t from-[#0B6E8F] to-[#34B3B0] transition-all" style={{ height: `${(m.eventi / max) * 100}%`, minHeight: 3 }} />
          <div className="text-[10px] text-[#7B929C]">{MESI[Number(m.mese.slice(5)) - 1]}</div>
        </div>
      ))}
    </div>
  );
}

export default function Today() {
  const [days, setDays] = useState(7);
  const [items, reload] = useList(`/reminders?days=${days}`);
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const done = async (key) => {
    await api.post("/reminders/done", { key });
    reload();
  };
  return (
    <div className="space-y-8">
      <PageHead eyebrow={new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} title="Oggi" />
      <WeatherPanel />
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 fade-up">
          <Stat dark label="Eventi del mese" value={s.mese.eventi} sub={fmtMoney(s.mese.valore)} testId="kpi-month" />
          <Stat label="Incassato nel mese" value={fmtMoney(s.incassato_mese)} sub={`Anno: ${fmtMoney(s.incassato_anno)}`} testId="kpi-cashed" />
          <Stat label="Da incassare" value={fmtMoney(s.da_incassare)} sub={s.scaduto ? `di cui scaduto ${fmtMoney(s.scaduto)}` : "nessuna rata scaduta"} testId="kpi-to-collect" />
          <Stat label="Preventivi accettati" value={`${s.preventivi.perc}%`} sub={`${s.preventivi.accettati} su ${s.preventivi.inviati} • valore medio evento ${fmtMoney(s.valore_medio)}`} testId="kpi-conversion" />
        </div>
      )}
      <PendingExtras />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <Panel className="lg:col-span-7" title="Da fare e clienti da contattare" testId="reminders-panel"
          right={<div className="flex gap-1">{[[0, "Oggi"], [7, "7 giorni"], [30, "30 giorni"]].map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`reminders-days-${d}`} className={`text-xs px-3 py-1.5 rounded-full border ${days === d ? "bg-[#0D2B3A] text-white border-[#0D2B3A]" : "border-[#D4E6EC]"}`}>{l}</button>))}</div>}>
          {items === null && <div className="text-sm text-[#7B929C]">Caricamento…</div>}
          {items && items.length === 0 && <div className="text-sm text-[#7B929C] py-6 text-center" data-testid="reminders-empty">Nessun promemoria: tutto in ordine.</div>}
          {items && items.map((r) => <Reminder key={r.key} r={r} done={done} />)}
        </Panel>
        <div className="lg:col-span-5 space-y-6">
          {s && (
            <Panel title="Prossimi 12 mesi" testId="months-panel">
              <MonthsChart months={s.mesi} />
              <div className="grid grid-cols-2 gap-3 text-xs text-[#47606B]">
                <div>Eventi dell'anno: <b>{s.anno.eventi}</b> ({fmtMoney(s.anno.valore)})</div>
                <div>Margine medio: <b>{s.margine_medio === null ? "—" : `${s.margine_medio}%`}</b></div>
              </div>
            </Panel>
          )}
          {s && (
            <Panel title="Prossimi eventi" testId="upcoming-panel">
              {s.prossimi.length === 0 && <div className="text-sm text-[#7B929C]">Nessun evento firmato in arrivo.</div>}
              {s.prossimi.map((e) => (
                <Link key={e.id} to={`/contratti/${e.id}`} className="flex justify-between gap-3 text-sm py-2 border-b border-[#EAF5F7] last:border-0 hover:text-[#0B6E8F]" data-testid={`upcoming-${e.number}`}>
                  <span><b>{e.cliente}</b> • {e.tipo}</span><span className="text-[#7B929C] text-xs">{itDate(e.data)} • {e.sede}</span>
                </Link>
              ))}
            </Panel>
          )}
          {s && Object.keys(s.lead.per_fonte).length > 0 && (
            <Panel title="Da dove arrivano i clienti" testId="sources-panel">
              {Object.entries(s.lead.per_fonte).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm"><span>{k}</span><span className="text-[#47606B]">{v.totale} richieste • {v.vinti} contratti ({v.totale ? Math.round((v.vinti / v.totale) * 100) : 0}%)</span></div>
              ))}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
